import React, { useEffect, useRef, useState } from "react";

/**
 * Pulsing badge + action popover on a candidate row. Extracted so the window-reset alert and the
 * refresh-limit pause alert share one implementation - previously the click-outside handling and
 * popover markup would have had to be duplicated per alert type.
 */

export interface AlertAction {
  label: string;
  /** Shown while running, so a slow request doesn't look like a dead button. */
  busyLabel?: string;
  onClick: () => void | Promise<void>;
  variant: "primary" | "danger" | "neutral";
}

interface CandidateAlertBadgeProps {
  label: string;
  /** Badge colour. Amber = needs attention, red = exam is halted right now. */
  tone: "amber" | "red";
  title: string;
  /** Optional context lines (reason, timestamp, count). Falsy entries are skipped. */
  detailLines?: (string | undefined | null | false)[];
  actions: AlertAction[];
}

const TONE = {
  amber: { badge: "bg-amber-500 hover:bg-amber-600", ping: "bg-amber-400" },
  red: { badge: "bg-red-600 hover:bg-red-700", ping: "bg-red-500" },
};

const ACTION_CLASS = {
  primary: "bg-green-600 text-white hover:bg-green-500",
  danger: "bg-red-600 text-white hover:bg-red-500",
  neutral:
    "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500",
};

const CandidateAlertBadge: React.FC<CandidateAlertBadgeProps> = ({
  label,
  tone,
  title,
  detailLines = [],
  actions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [busyIndex, setBusyIndex] = useState<number | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const runAction = async (action: AlertAction, index: number) => {
    if (busyIndex !== null) return;
    setBusyIndex(index);
    try {
      await action.onClick();
      setIsOpen(false);
    } finally {
      setBusyIndex(null);
    }
  };

  const visibleDetails = detailLines.filter(Boolean) as string[];

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        // stopPropagation is required: the row itself is clickable, so without it opening the
        // popover would also select/open the candidate.
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((o) => !o);
        }}
        className={`relative inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold text-white focus:outline-none ${TONE[tone].badge}`}
      >
        <span
          className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 pointer-events-none ${TONE[tone].ping}`}
        />
        <svg
          className="relative h-3 w-3 shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
          />
        </svg>
        <span className="relative">{label}</span>
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full z-20 mt-1 w-72 rounded-md bg-white dark:bg-darkbglight shadow-lg ring-1 ring-black ring-opacity-5 p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{title}</p>
          {visibleDetails.map((line, i) => (
            <p key={i} className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {line}
            </p>
          ))}
          <div className="flex flex-wrap gap-2 mt-3">
            {actions.map((action, index) => (
              <button
                key={action.label}
                type="button"
                onClick={() => runAction(action, index)}
                disabled={busyIndex !== null}
                className={`flex-1 min-w-[6rem] rounded-md px-3 py-1.5 text-sm font-semibold disabled:opacity-60 ${ACTION_CLASS[action.variant]}`}
              >
                {busyIndex === index ? action.busyLabel ?? "Working..." : action.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateAlertBadge;
